import React, { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, ArrowRight, BadgeCheck, Layers, Loader2, MapPin, Search, Store, X } from 'lucide-react';
import { getPublicVendors } from '../api/user.api';
import { getPublicSiteOrigin } from '../utils/publicSiteOrigin';
import PublicHeader from './PublicHeader';

const BrowseVendorsView = ({ setActiveView, showNotification, isAuthenticated, onSupportClick }) => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [searchDraft, setSearchDraft] = useState('');
  const [search, setSearch] = useState('');

  const fetchPage = useCallback(
    async (pageNum, append) => {
      if (append) setLoadingMore(true);
      else setLoading(true);
      try {
        const data = await getPublicVendors({ page: pageNum, limit: 24, search });
        const next = data.vendors || [];
        setVendors((prev) => (append ? [...prev, ...next] : next));
        setPagination(data.pagination || null);
        setPage(pageNum);
      } catch (e) {
        console.error(e);
        showNotification?.(e.response?.data?.message || 'Could not load vendors', 'error');
        if (!append) setVendors([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [search, showNotification]
  );

  useEffect(() => {
    fetchPage(1, false);
  }, [fetchPage]);

  useEffect(() => {
    const id = setTimeout(() => {
      setSearch((prev) => {
        const next = searchDraft.trim();
        return prev === next ? prev : next;
      });
    }, 450);
    return () => clearTimeout(id);
  }, [searchDraft]);

  const vendorStorefrontUrl = (vendor) => {
    const slug = vendor?.publicUsername || vendor?.phoneDigits;
    return slug ? `${getPublicSiteOrigin()}/${slug}` : '#';
  };

  const goBack = () => {
    if (window.location.pathname === '/browse-vendors') {
      window.history.replaceState({}, '', '/');
    }
    setActiveView(isAuthenticated ? 'generate' : 'home');
  };

  const applySearch = (e) => {
    e?.preventDefault?.();
    const q = searchDraft.trim();
    setSearch(q);
  };

  const clearSearch = () => {
    setSearchDraft('');
    setSearch('');
  };

  const loadMore = () => {
    if (!pagination?.hasNextPage || loadingMore) return;
    fetchPage(page + 1, true);
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-violet-50 via-indigo-50/70 to-fuchsia-50"
      style={{ colorScheme: 'light' }}
    >
      {!isAuthenticated && (
        <PublicHeader
          activeTab="browse-vendors"
          activeViewId="browse-vendors"
          setActiveView={setActiveView}
          onLaunchAI={() => setActiveView('login')}
          onSupportClick={onSupportClick}
        />
      )}

      <main
        className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 sm:pb-10 ${
          isAuthenticated ? 'pt-6 sm:pt-8' : 'pt-24 sm:pt-28'
        }`}
      >
        <div className="mb-7 sm:mb-8">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={goBack}
              className="p-2.5 hover:bg-white/70 rounded-xl transition-all shrink-0 border border-slate-200 bg-white/60 backdrop-blur shadow-sm"
              title="Back"
            >
              <ArrowLeft size={20} className="sm:w-5 sm:h-5" />
            </button>
            <div className="min-w-0 flex-1">
              <h1 className="text-lg sm:text-xl font-bold text-slate-900 tracking-tight">Browse vendors</h1>
              <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
                Sorted by public fabrics in collections (most first)
              </p>
            </div>
          </div>

          <form onSubmit={applySearch} className="mt-4 flex flex-col sm:flex-row gap-2 sm:gap-3 max-w-2xl">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={18} />
              <input
                type="search"
                value={searchDraft}
                onChange={(e) => setSearchDraft(e.target.value)}
                placeholder="Name, phone, brand, bio, location, website, email, specialties…"
                aria-label="Search vendors"
                className="w-full pl-10 py-2.5 rounded-xl border border-slate-200 bg-white/90 text-slate-900 text-sm focus:ring-2 focus:ring-violet-200 focus:border-violet-400 outline-none pr-10"
              />
              {searchDraft ? (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                  aria-label="Clear search"
                >
                  <X size={18} />
                </button>
              ) : null}
            </div>
            <button
              type="submit"
              className="px-5 py-2.5 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition-colors shrink-0"
            >
              Search
            </button>
          </form>
          <p className="text-xs text-slate-500 max-w-2xl mt-2">
            Matches vendor name, phone number, company, account email, and public profile fields (brand, tagline, bio,
            address, services, social links, and more). Results update as you type.
          </p>
        </div>
        {loading ? (
          <div className="flex flex-col items-center justify-center py-24 gap-3">
            <Loader2 className="w-10 h-10 text-violet-600 animate-spin" />
            <p className="text-slate-600 text-sm">Loading vendors…</p>
          </div>
        ) : vendors.length === 0 ? (
          <div className="text-center py-20 px-4">
            <Store className="w-14 h-14 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-700 text-lg font-medium">No vendors found</p>
            <p className="text-slate-500 text-sm mt-2 max-w-md mx-auto">
              {search
                ? 'Try a different search term.'
                : 'There are no vendor accounts to show yet.'}
            </p>
          </div>
        ) : (
          <>
            <p className="text-sm text-slate-600 mb-6">
              {pagination?.totalVendors != null ? (
                <>
                  <span className="font-semibold text-slate-800">{pagination.totalVendors}</span> vendor
                  {pagination.totalVendors !== 1 ? 's' : ''}
                </>
              ) : (
                <>
                  <span className="font-semibold text-slate-800">{vendors.length}</span> shown
                </>
              )}
            </p>

            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 list-none p-0 m-0">
              {vendors.map((v) => {
                const title = v.brandName || v.name || 'Vendor';
                const subtitle = v.brandName && v.name && v.brandName !== v.name ? v.name : v.tagline;
                const fabricCount = v.publicFabricCount ?? 0;
                return (
                  <li key={v.phoneDigits} className="flex h-full min-h-0">
                    <a
                      href={vendorStorefrontUrl(v)}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={`Open ${title} in a new tab`}
                      className="flex flex-1 flex-col w-full min-h-0 text-left rounded-2xl border border-slate-200 bg-white shadow-sm hover:shadow-lg hover:border-violet-300/60 hover:-translate-y-0.5 transition-all duration-200 overflow-hidden group no-underline text-inherit"
                    >
                      {/* Slim cover / brand strip */}
                      <div className="relative h-16 sm:h-[4.5rem] shrink-0 bg-gradient-to-br from-violet-600 via-fuchsia-600 to-indigo-700">
                        {v.coverUrl ? (
                          <img
                            src={v.coverUrl}
                            alt=""
                            className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300"
                          />
                        ) : null}
                        <div
                          className="absolute inset-0 bg-gradient-to-r from-violet-900/35 via-transparent to-fuchsia-900/25 pointer-events-none"
                          aria-hidden
                        />
                      </div>

                      <div className="flex flex-1 flex-col px-4 pb-4 pt-3">
                        <div className="flex gap-3 items-start">
                          <div className="w-[3.75rem] h-[3.75rem] sm:w-16 sm:h-16 rounded-2xl border-2 border-white shadow-md ring-1 ring-slate-200/80 overflow-hidden bg-gradient-to-br from-violet-50 to-fuchsia-50 flex items-center justify-center shrink-0 -mt-9 relative z-[1]">
                            {v.avatarUrl ? (
                              <img src={v.avatarUrl} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <Store className="w-8 h-8 text-violet-600" aria-hidden />
                            )}
                          </div>
                          <div className="flex-1 min-w-0 pt-1">
                            <div className="flex flex-wrap items-center gap-2 gap-y-1.5">
                              <h2 className="text-base sm:text-[1.05rem] font-bold text-slate-900 leading-snug group-hover:text-violet-700 transition-colors line-clamp-2">
                                {title}
                              </h2>
                              <span
                                className="inline-flex items-center gap-0.5 rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white shadow-sm shrink-0"
                                title="Verified vendor on Fashdeck"
                              >
                                <BadgeCheck size={12} className="text-white" strokeWidth={2.5} aria-hidden />
                                Verified
                              </span>
                            </div>
                            {subtitle ? (
                              <p className="text-sm text-slate-500 mt-1.5 line-clamp-2 leading-snug">{subtitle}</p>
                            ) : null}
                          </div>
                        </div>

                        <div className="mt-4 space-y-2.5 text-sm border-t border-slate-100 pt-3">
                          <p className="flex items-start gap-2 text-slate-600">
                            <MapPin size={16} className="shrink-0 text-violet-500 mt-0.5" aria-hidden />
                            <span className={v.location ? 'line-clamp-2' : 'text-slate-400 italic'}>
                              {v.location || 'Location not listed'}
                            </span>
                          </p>
                          <p className="flex items-start gap-2 text-slate-600">
                            <Layers size={16} className="shrink-0 text-violet-500 mt-0.5" aria-hidden />
                            <span>
                              <span className="font-semibold text-slate-800 tabular-nums">{fabricCount}</span>
                              {fabricCount === 1 ? ' public fabric' : ' public fabrics'} in collections
                            </span>
                          </p>
                        </div>

                        <div className="mt-auto pt-4 flex items-center justify-between gap-2 border-t border-slate-100">
                          <span className="text-sm font-semibold text-violet-600 group-hover:text-violet-700">
                            View storefront
                          </span>
                          <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-violet-50 text-violet-600 group-hover:bg-violet-100 transition-colors">
                            <ArrowRight size={18} className="group-hover:translate-x-0.5 transition-transform" aria-hidden />
                          </span>
                        </div>
                      </div>
                    </a>
                  </li>
                );
              })}
            </ul>

            {pagination?.hasNextPage ? (
              <div className="flex justify-center mt-10">
                <button
                  type="button"
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="px-6 py-3 rounded-xl border border-slate-200 bg-white text-slate-800 font-semibold text-sm hover:bg-slate-50 disabled:opacity-60 inline-flex items-center gap-2"
                >
                  {loadingMore ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Loading…
                    </>
                  ) : (
                    'Load more'
                  )}
                </button>
              </div>
            ) : null}
          </>
        )}
      </main>
    </div>
  );
};

export default BrowseVendorsView;
